import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import TimePickerInput from '@/components/TimePickerInput'
import { Card } from '@/components/ui/card'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import AddIcons from '@/icons/AddIcons'
import EyeIcons from '@/icons/EyeIcons'
import { getEmployeeContains } from '@/modules/Employee/services'
import { CV_SCREEN_RESULTS, FINAL_STATUS } from '@/modules/Resume/constants'
import {
    getCVPreviewEmail,
    getHRTemplates,
    sendMailInterview,
} from '@/modules/Resume/services'
import { InterviewMailValues } from '@/modules/Resume/types'
import { paths } from '@/utils/path'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
    Button,
    CloseIcon,
    Dialog,
    FormItem,
    IconButton,
    Typogaphy,
} from 'vnetwork-ui'
import * as yup from 'yup'

interface LeaderInput {
    employee: {
        id: number
        label: string
    } | null
    position: string
}
const schema = yup.object().shape({
    leaders: yup
        .array()
        .min(1, 'Phải có ít nhất 1 người phỏng vấn')
        .required('Không được để trống')
        .of(
            yup.object({
                employee: yup.mixed().required('Chưa chọn nhân sự'),
                position: yup.string().required('Chưa nhập vị trí'),
            })
        ),
    interview_time: yup
        .mixed()
        .nullable()
        .required('Chưa chọn thời gian')
        .test(
            'is-valid-dayjs',
            'Thời gian không hợp lệ',
            (val) => dayjs.isDayjs(val) && val.isValid()
        ),

    interview_date: yup
        .date()
        .nullable()
        .typeError('Chưa chọn ngày')
        .required('Chưa chọn ngày'),

    location: yup.string().required('Chưa nhập địa điểm'),

    note: yup.string().required('Chưa nhập ghi chú'),
})
interface FormValues {
    mailer_template_id: number | null
    leaders: LeaderInput[]
    interview_time: Dayjs | null
    interview_date: Dayjs | null
    location: string
    note: string
    cc: { id: number; name: string; email: string }[]
}

interface SendMailInterviewDialogProps {
    open: boolean
    onClose: () => void
    updateData: Record<string, any>
    refetch: () => void
}

const SendMailInterviewDialog: React.FC<SendMailInterviewDialogProps> = ({
    open,
    onClose,
    refetch,
    updateData,
}) => {
    console.log('[DEBUG] updateData', updateData)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [showPreview, setShowPreview] = useState(false)
    // Helper function to get next business day (skip Saturday and Sunday)
    const getNextBusinessDay = () => {
        let nextDay = dayjs().add(1, 'day')
        // If next day is Saturday (6) or Sunday (0), move to Monday
        while (nextDay.day() === 6 || nextDay.day() === 0) {
            nextDay = nextDay.add(1, 'day')
        }
        return nextDay.toDate()
    }

    const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
        defaultValues: {
            mailer_template_id: null,
            leaders: [{ employee: null, position: '' }],
            interview_time: dayjs().hour(9).minute(30).second(0).millisecond(0),
            interview_date: getNextBusinessDay(),
            location:
                'Phòng 23-06, tầng 23 tòa nhà UOA Tower, số 6 Tân Trào, Phường Tân Mỹ, Thành phố Hồ Chí Minh.',
            note: `- Gửi xe tại tầng hầm của Tòa nhà (có 3 tầng hầm), sau đó di chuyển bằng thang máy lên tầng G.\n- Mang CMND/ CCCD để check in tại quầy lễ tân khi đến toà nhà UOA, sau đó theo sự hướng dẫn của lễ tân dùng thang máy LP4/ 5 / 6 để lên tầng 23. \n- Thông tin liên hệ: HR: 0949976536`,
            cc: [],
        },
        //@ts-ignore
        resolver: yupResolver<FormValues>(schema),
    })

    // Helper function to get day of week in Vietnamese
    const getDayOfWeek = (date: Date | Dayjs | null) => {
        if (!date) return ''
        const dayjsDate = dayjs.isDayjs(date) ? date : dayjs(date)
        const dayNames = [
            'Chủ nhật',
            'Thứ hai',
            'Thứ ba',
            'Thứ tư',
            'Thứ năm',
            'Thứ sáu',
            'Thứ bảy',
        ]
        return dayNames[dayjsDate.day()]
    }
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'leaders',
    })

    // Watch all leaders to auto-fill position
    const watchedLeaders = useWatch({
        control,
        name: 'leaders',
    })

    // Auto-fill position when employee is selected
    useEffect(() => {
        if (!watchedLeaders) return

        watchedLeaders.forEach(async (leader, index) => {
            if (leader?.employee) {
                const employeeId =
                    typeof leader.employee === 'object' &&
                    'value' in leader.employee
                        ? leader.employee.value
                        : typeof leader.employee === 'number'
                          ? leader.employee
                          : null

                if (employeeId && !leader.position) {
                    try {
                        const employeeInfo = await getEmployeeContains({
                            employee_ids: [employeeId as number],
                        })
                        const positionName =
                            employeeInfo?.[0]?.position_name || ''
                        if (positionName) {
                            setValue(`leaders.${index}.position`, positionName)
                        }
                    } catch (error) {
                        console.error(
                            'Error fetching employee position:',
                            error
                        )
                    }
                }
            }
        })
    }, [watchedLeaders, setValue])

    const mailTemplate =
        CV_SCREEN_RESULTS?.find(
            (item) => item?.value === updateData?.cover_letter_screen_result
        )?.mail_template ||
        FINAL_STATUS?.find((item) => item?.value === updateData?.final_status)
            ?.mail_template

    const { data: hrTemplatesData, renderState: hrTemplatesRenderState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.RESUMES, 'hr-templates'],
            queryFn: () => getHRTemplates(),
            enabled: open,
        })

    const hrTemplateOptions =
        hrTemplatesData?.map((template) => ({
            label: template.mailer_template_subject,
            value: template.mailer_template_id,
        })) || []

    const { mutateAsync: sendMailInterviewMutation, isPending } =
        useMutationWithHandler(
            {
                mutationFn: sendMailInterview,
            },
            {
                reactQueryKeys: QUERY_KEYS.RESUMES,
                successMessage: t('send-mail-success'),
            }
        )

    const watchedValues = watch()
    const { mutateAsync: previewEmailMutation, isPending: isPreviewing } =
        useMutationWithHandler(
            {
                mutationFn: async () => {
                    if (!updateData?.id) {
                        throw new Error('Missing CV ID or mail template')
                    }

                    const basePayload = transformFormToPayload(watchedValues)
                    const leaderIds = watchedValues.leaders
                        .map((c) =>
                            typeof c.employee === 'object' && 'id' in c
                                ? c.employee?.id
                                : typeof c.employee === 'number'
                                  ? c.employee
                                  : null
                        )
                        .filter((id): id is number => !!id)

                    const leaderInfos = await getEmployeeContains({
                        employee_ids: leaderIds,
                    })

                    const leaderMap = new Map(
                        leaderInfos.map((info) => [
                            info.value,
                            { name: info.label, email: info.email },
                        ])
                    )

                    const leaders = watchedValues.leaders.map((l) => {
                        const emp = l.employee
                        const id =
                            emp && typeof emp === 'object'
                                ? emp.id
                                : typeof emp === 'number'
                                  ? emp
                                  : null

                        const empInfo =
                            id !== null && leaderMap.has(id)
                                ? leaderMap.get(id)!
                                : null
                        const label = empInfo ? empInfo.name : ''

                        return {
                            id,
                            label,
                            position: l.position || '',
                        }
                    })

                    const ccIds = watchedValues.cc
                        .map((c) =>
                            typeof c === 'object' && 'id' in c
                                ? c.id
                                : typeof c === 'number'
                                  ? c
                                  : null
                        )
                        .filter((id): id is number => !!id)

                    const ccInfos = await getEmployeeContains({
                        employee_ids: ccIds,
                    })

                    const ccEmails = ccInfos
                        .map((info) => info.email)
                        .filter((email): email is string => !!email)

                    const mailSend = [
                        ...(updateData?.mail_send || []),
                        mailTemplate,
                    ].filter((item): item is string => Boolean(item))

                    const payload: InterviewMailValues = {
                        ...basePayload,
                        leaders,
                        cc: ccEmails,
                        mailer_template_id: watchedValues.mailer_template_id,
                        mail_send: mailSend,
                    }

                    return getCVPreviewEmail({
                        cvID: updateData.id,
                        payload,
                    })
                },
            },
            {
                errorMessage: t('send-mail-error'),
                reactQueryKeys: QUERY_KEYS.RESUMES,
            }
        )

    const [previewEmailData, setPreviewEmailData] = useState<string | null>(
        null
    )
    const transformFormToPayload = (
        values: FormValues
    ): Omit<
        InterviewMailValues,
        'mailTemplate' | 'mail_send' | 'cc' | 'leaders'
    > => {
        const time = values.interview_time?.format('hh:mm A')

        return {
            interview_time: time?.split(' ')[0] || '',
            interview_date: values.interview_date
                ? ` - ngày ${dayjs(values.interview_date).format('DD/MM/YYYY')}`
                : '',
            location: values.location,
            note: values.note,
            mailer_template_id: values.mailer_template_id,
        }
    }
    const onSubmit = async (values: FormValues) => {
        try {
            if (!updateData?.id) {
                toast.error(t('send-mail-error.'))
                return
            }

            const basePayload = transformFormToPayload(values)
            const leaderIds = values.leaders
                .map((c) =>
                    typeof c.employee === 'object' && 'id' in c
                        ? c.employee?.id
                        : typeof c.employee === 'number'
                          ? c.employee
                          : null
                )
                .filter((id): id is number => !!id)

            const leaderInfos = await getEmployeeContains({
                employee_ids: leaderIds,
            })

            const leaderMap = new Map(
                leaderInfos.map((info) => [
                    info.value,
                    { name: info.label, email: info.email },
                ])
            )

            const leaders = values.leaders.map((l) => {
                const emp = l.employee
                const id =
                    emp && typeof emp === 'object'
                        ? emp.id
                        : typeof emp === 'number'
                          ? emp
                          : null

                const empInfo =
                    id !== null && leaderMap.has(id) ? leaderMap.get(id)! : null
                const label = empInfo ? empInfo.name : ''

                return {
                    id,
                    label,
                    position: l.position || '',
                }
            })

            const ccIds = values.cc
                .map((c) =>
                    typeof c === 'object' && 'id' in c
                        ? c.id
                        : typeof c === 'number'
                          ? c
                          : null
                )
                .filter((id): id is number => !!id)

            const ccInfos = await getEmployeeContains({
                employee_ids: ccIds,
            })

            const ccEmails = ccInfos
                .map((info) => info.email)
                .filter((email): email is string => !!email)

            const mailSend = [
                ...(updateData?.mail_send || []),
                mailTemplate,
            ].filter((item): item is string => Boolean(item))

            const payload: InterviewMailValues = {
                ...basePayload,
                leaders,
                cc: ccEmails,
                mailer_template_id: values.mailer_template_id,
                mail_send: mailSend,
            }
            console.log('[DEBUG] Form Submit Payload:', payload)
            await sendMailInterviewMutation({
                id: updateData?.id,
                values: payload,
            })
            refetch()
            onClose()
        } catch (err) {
            console.error(err)
        }
    }
    return (
        <Dialog
            onClose={onClose}
            open={open}
            title={t('send-mail-title-dialog')}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Card className="p-4 mb-4">
                    <div className="flex items-center justify-between mb-4">
                        <Typogaphy className="font-medium text-base">
                            {t('interviewers')}
                        </Typogaphy>
                        <Button
                            startIcon={<AddIcons />}
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={() =>
                                append({ employee: null, position: '' })
                            }
                        >
                            {t('add')}
                        </Button>
                    </div>
                    <div className="space-y-4">
                        {fields.map((field, index) => (
                            <div key={field.id}>
                                <div className="grid md:grid-cols-2 grid-cols-1 gap-2">
                                    <div className="col-span-1">
                                        <EmployeeSelect
                                            onlyActive
                                            control={control}
                                            name={`leaders.${index}.employee`}
                                            label={t('interviewer')}
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <div className="flex items-center gap-2">
                                            <FormItem
                                                control={control}
                                                name={`leaders.${index}.position`}
                                                label={t('position')}
                                                type="textfield"
                                                className="flex-1"
                                            />
                                            {fields?.length > 1 && (
                                                <IconButton
                                                    size="small"
                                                    className="border"
                                                    color="error"
                                                    onClick={() =>
                                                        remove(index)
                                                    }
                                                >
                                                    <CloseIcon color="primary" />
                                                </IconButton>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
                <div className="mb-4">
                    <EmployeeSelect
                        control={control}
                        name="cc"
                        multiple
                        label="CC"
                    />
                </div>

                <div className="mb-4">
                    {hrTemplatesRenderState() ? (
                        hrTemplatesRenderState()
                    ) : (
                        <div className="flex gap-2 items-end">
                            <div className="flex-1">
                                <FormItem
                                    control={control}
                                    name="mailer_template_id"
                                    type="select"
                                    label={t('mailer-template')}
                                    options={hrTemplateOptions}
                                    allowClear
                                />
                            </div>
                            {watch('mailer_template_id') && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    type="button"
                                    onClick={() => {
                                        navigate(
                                            paths?.mailerTemplateDetail?.replace(
                                                ':id',
                                                String(
                                                    watch('mailer_template_id')
                                                )
                                            )
                                        )
                                    }}
                                >
                                    {t('view-edit-template')}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
                <div className="mb-4">
                    <FormItem
                        control={control}
                        name="interview_date"
                        type="datepicker"
                        label={
                            watch('interview_date')
                                ? `${t('interview-date')} (${getDayOfWeek(watch('interview_date') as Date | Dayjs | null)})`
                                : t('interview-date')
                        }
                    />
                </div>
                <div className="mb-4">
                    <TimePickerInput
                        value={watch('interview_time')?.format('HH:mm') || ''}
                        onChange={(val) => {
                            if (val) {
                                const [h, m] = val.split(':')
                                const newTime = dayjs()
                                    .hour(+h)
                                    .minute(+m)
                                    .second(0)
                                setValue('interview_time', newTime)
                            } else {
                                setValue('interview_time', null)
                            }
                        }}
                        disabledTimeRanges={[{ start: '12:00', end: '13:00' }]}
                    />
                </div>
                <div className="mb-4">
                    <FormItem
                        control={control}
                        name="location"
                        type="textfield"
                        label={t('interview-location')}
                    />
                </div>
                <TextEditorCustomize
                    value={{
                        text: watch('note'),
                        attachments: [],
                    }}
                    onChange={(val) => {
                        setValue('note', val.text)
                    }}
                    placeholder={t('interview-note')}
                    label={t('interview-note')}
                />
                <div className="flex justify-end mt-4 gap-4">
                    <Button
                        size="small"
                        variant="outlined"
                        type="button"
                        startIcon={<EyeIcons />}
                        onClick={async () => {
                            try {
                                const previewData = await previewEmailMutation()
                                setPreviewEmailData(previewData)
                                setShowPreview(true)
                            } catch (error) {
                                console.error('Preview error:', error)
                            }
                        }}
                        disabled={!updateData?.id || isPreviewing}
                        loading={isPreviewing}
                    >
                        {t('preview')}
                    </Button>
                    <Button size="small" variant="outlined" onClick={onClose}>
                        {t('cancle-operation')}
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        loading={isPending}
                        // onClick={handleSubmit(onSubmit)}
                    >
                        {t('send-email-now')}
                    </Button>
                </div>
            </form>

            {/* Preview Email Dialog */}
            <Dialog
                onClose={() => {
                    setShowPreview(false)
                    setPreviewEmailData(null)
                }}
                open={showPreview}
                maxWidth="lg"
            >
                {isPreviewing ? (
                    <div className="p-4 text-center">{t('loading...')}</div>
                ) : previewEmailData ? (
                    <div>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: previewEmailData,
                            }}
                        />
                    </div>
                ) : null}
            </Dialog>
        </Dialog>
    )
}

export default SendMailInterviewDialog
